import { useEffect, useRef } from "react";
import * as THREE from "three";
import { sampleAccentGradient } from "../../lib/accentGradient";

interface FieldNode {
  base: THREE.Vector3;
  phase: number;
}

function buildNodes(count: number, width: number, height: number, depth: number): FieldNode[] {
  const nodes: FieldNode[] = [];
  for (let i = 0; i < count; i++) {
    nodes.push({
      base: new THREE.Vector3(
        (Math.random() - 0.5) * width,
        (Math.random() - 0.5) * height,
        (Math.random() - 0.5) * depth
      ),
      phase: Math.random() * Math.PI * 2,
    });
  }
  return nodes;
}

function buildEdges(nodes: FieldNode[], connectDistance: number): [number, number][] {
  const edges: [number, number][] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const nodeI = nodes[i] as FieldNode;
      const nodeJ = nodes[j] as FieldNode;
      if (nodeI.base.distanceTo(nodeJ.base) < connectDistance) {
        edges.push([i, j]);
      }
    }
  }
  return edges;
}

export interface NodeFieldProps {
  className?: string;
  title?: string;
  nodeCount?: number;
  connectDistance?: number;
  fieldWidth?: number;
  fieldHeight?: number;
  fieldDepth?: number;
  cameraDistance?: number;
  pointSize?: number;
}

/**
 * Lightweight companion to LowPolyTerrain: a sparse drifting node graph
 * instead of a faceted terrain mesh — cheap enough to sit behind page copy.
 * Defaults are tuned for a wide hero band; pass smaller field/count for
 * compact spots like the header brand mark.
 */
export function NodeField({
  className,
  title = "Woven network graph",
  nodeCount = 42,
  connectDistance = 2.4,
  fieldWidth = 16,
  fieldHeight = 5,
  fieldDepth = 5,
  cameraDistance = 9,
  pointSize = 0.09,
}: NodeFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x020812, 0.06);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 0, cameraDistance);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const nodes = buildNodes(nodeCount, fieldWidth, fieldHeight, fieldDepth);
    const edges = buildEdges(nodes, connectDistance);

    const pointPositions = new Float32Array(nodes.length * 3);
    const pointColors = new Float32Array(nodes.length * 3);
    nodes.forEach((node, i) => {
      pointPositions[i * 3] = node.base.x;
      pointPositions[i * 3 + 1] = node.base.y;
      pointPositions[i * 3 + 2] = node.base.z;
      const color = sampleAccentGradient((node.base.x + fieldWidth / 2) / fieldWidth);
      pointColors[i * 3] = color.r;
      pointColors[i * 3 + 1] = color.g;
      pointColors[i * 3 + 2] = color.b;
    });

    const pointGeometry = new THREE.BufferGeometry();
    pointGeometry.setAttribute("position", new THREE.BufferAttribute(pointPositions, 3));
    pointGeometry.setAttribute("color", new THREE.BufferAttribute(pointColors, 3));
    const points = new THREE.Points(
      pointGeometry,
      new THREE.PointsMaterial({
        size: pointSize,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        sizeAttenuation: true,
      })
    );

    const linePositions = new Float32Array(edges.length * 6);
    const lineColors = new Float32Array(edges.length * 6);
    edges.forEach(([a, b], i) => {
      const nodeA = nodes[a] as FieldNode;
      const nodeB = nodes[b] as FieldNode;
      linePositions.set([nodeA.base.x, nodeA.base.y, nodeA.base.z], i * 6);
      linePositions.set([nodeB.base.x, nodeB.base.y, nodeB.base.z], i * 6 + 3);
      const colorA = sampleAccentGradient((nodeA.base.x + fieldWidth / 2) / fieldWidth);
      const colorB = sampleAccentGradient((nodeB.base.x + fieldWidth / 2) / fieldWidth);
      lineColors.set([colorA.r, colorA.g, colorA.b], i * 6);
      lineColors.set([colorB.r, colorB.g, colorB.b], i * 6 + 3);
    });

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute("position", new THREE.BufferAttribute(linePositions, 3));
    lineGeometry.setAttribute("color", new THREE.BufferAttribute(lineColors, 3));
    const lines = new THREE.LineSegments(
      lineGeometry,
      new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.22 })
    );

    scene.add(points, lines);

    function resize() {
      const { clientWidth, clientHeight } = container as HTMLDivElement;
      if (clientWidth === 0 || clientHeight === 0) {
        return;
      }
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
      renderer.render(scene, camera);
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    let rafId = 0;
    if (!reduceMotion) {
      const clock = new THREE.Clock();
      const pointPosition = pointGeometry.attributes.position as THREE.BufferAttribute;
      const linePosition = lineGeometry.attributes.position as THREE.BufferAttribute;
      const animate = () => {
        const t = clock.getElapsedTime();
        nodes.forEach((node, i) => {
          pointPosition.setY(i, node.base.y + Math.sin(t * 0.4 + node.phase) * 0.18);
        });
        pointPosition.needsUpdate = true;

        edges.forEach(([a, b], i) => {
          const nodeA = nodes[a] as FieldNode;
          const nodeB = nodes[b] as FieldNode;
          linePosition.setY(i * 2, nodeA.base.y + Math.sin(t * 0.4 + nodeA.phase) * 0.18);
          linePosition.setY(i * 2 + 1, nodeB.base.y + Math.sin(t * 0.4 + nodeB.phase) * 0.18);
        });
        linePosition.needsUpdate = true;

        scene.rotation.y = Math.sin(t * 0.08) * 0.08;
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(animate);
      };
      rafId = requestAnimationFrame(animate);
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      container.removeChild(renderer.domElement);
      renderer.dispose();
      pointGeometry.dispose();
      lineGeometry.dispose();
      (points.material as THREE.Material).dispose();
      (lines.material as THREE.Material).dispose();
    };
  }, [nodeCount, connectDistance, fieldWidth, fieldHeight, fieldDepth, cameraDistance, pointSize]);

  return (
    <div
      ref={containerRef}
      className={className ? `node-field-3d ${className}` : "node-field-3d"}
      role="img"
      aria-label={title}
    />
  );
}
